import * as React from 'react'
import { Outlet, useLocation, useParams } from 'react-router-dom'

import { HistoricalContractsImportPageDocument } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import NuvoImporter from 'modules/universal_importer/components/NuvoImporter'

type LocationStateType = {
  selectedLegalEntity?: string
} | null

const HistoricalContractsImportPage = () => {
  const {
    company,
    access: { locale },
  } = useCurrentSession()

  const { legal_entity_id: legalEntityIdFromParams } = useParams<{ legal_entity_id?: string }>()
  const location = useLocation()
  const locationState: LocationStateType = location.state
  const selectedLegalEntityId = locationState?.selectedLegalEntity ?? legalEntityIdFromParams

  const nuvo_import_type = 'historical_contract_version_importer'
  const { data, loading, error } = useQuery(HistoricalContractsImportPageDocument, {
    variables: { companyId: company.id, legalEntityId: selectedLegalEntityId ?? '' },
    fetchPolicy: 'network-only',
    skip: !selectedLegalEntityId,
  })

  const canImportResources = data?.universalImporter._policies.importResources.can
  const canEditContracts = data?.contracts._policies.contractEditContractVersion.can
  const hasDevUniversalContractsImportFeature =
    data?.features.featuresConnection.nodes.find(
      (feature) => feature.name === 'dev_universal_contracts_import'
    )?.enabled ?? false

  const authorized = canImportResources && canEditContracts && hasDevUniversalContractsImportFeature

  const legalEntityCountry = data?.companies.legalEntitiesConnection.nodes[0]?.country
  const legalEntitiesIds = React.useMemo(
    () => (selectedLegalEntityId ? [BigInt(selectedLegalEntityId)] : []),
    [selectedLegalEntityId]
  )
  const metadata = {
    legal_entity_ids: legalEntitiesIds,
    legal_entity_countries: legalEntityCountry ? [legalEntityCountry] : [],
  }

  if (loading) {
    return <Loading />
  }

  return (
    <ResponsivePage.Body
      authorized={authorized}
      loading={loading}
      error={!selectedLegalEntityId || error}
      showHeader={false}
    >
      <NuvoImporter
        importType={nuvo_import_type}
        locale={locale}
        companyId={company.id}
        metadata={metadata}
      />
      <Outlet />
    </ResponsivePage.Body>
  )
}

export default HistoricalContractsImportPage
